import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PencilIcon } from "@heroicons/react/24/outline";

export default function UpdateProduct({
  updateProductData,
  updateModalSetting,
  handlePageUpdate,
}) {
  const [product, setProduct] = useState({
    product_id: updateProductData.product_id || "",
    product_name: updateProductData.product_name || "",
    price_per_unit: updateProductData.price_per_unit || "",
    purchase_count: updateProductData.purchase_count || 0,
    sale_count: updateProductData.sale_count || 0,
    in_stock: updateProductData.in_stock || 0,
    location: updateProductData.location || "",
  });
  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    setProduct({
      product_id: updateProductData.product_id || "",
      product_name: updateProductData.product_name || "",
      price_per_unit: updateProductData.price_per_unit || "",
      purchase_count: updateProductData.purchase_count || 0,
      sale_count: updateProductData.sale_count || 0,
      in_stock: updateProductData.in_stock || 0,
      location: updateProductData.location || "",
    });
  }, [updateProductData]);

  const handleInputChange = (key, value) => {
    setProduct({ ...product, [key]: value });
  };

  const updateProduct = () => {
    console.log("Updating product with ID:", product.product_id);
    if (!product.product_id) {
      console.error("Product ID is undefined");
      alert("Product ID is missing");
      return;
    }

    const payload = {
      product_id: product.product_id,
      product_name: product.product_name,
      price_per_unit: parseFloat(product.price_per_unit),
      purchase_count: parseInt(product.purchase_count),
      sale_count: parseInt(product.sale_count),
      in_stock: parseInt(product.in_stock),
      location: product.location.trim() || null,
    };

    fetch("https://stock.krishtec.co.in/stock/update_product.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "Product updated successfully") {
          alert("Product updated successfully");
          setOpen(false);
          updateModalSetting();
          handlePageUpdate();
        } else {
          alert("Error: " + data.message);
        }
      })
      .catch((err) => {
        console.log("Update error:", err);
        alert("Failed to update product");
      });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          setOpen(false);
          updateModalSetting();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PencilIcon
                        className="h-6 w-6 text-blue-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Update Product
                      </Dialog.Title>
                      <form action="#">
                        <div className="grid gap-4 mb-4 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <label
                              htmlFor="product_name"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              Product Name
                            </label>
                            <input
                              type="text"
                              name="product_name"
                              id="product_name"
                              value={product.product_name}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., Soldering Iron"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="price_per_unit"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              Price Per Unit
                            </label>
                            <input
                              type="number"
                              name="price_per_unit"
                              id="price_per_unit"
                              value={product.price_per_unit}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., 10.50"
                              step="0.01"
                              min="0"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="purchase_count"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              Purchase Count
                            </label>
                            <input
                              type="number"
                              name="purchase_count"
                              id="purchase_count"
                              value={product.purchase_count}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., 10"
                              min="0"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="sale_count"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              Sale Count
                            </label>
                            <input
                              type="number"
                              name="sale_count"
                              id="sale_count"
                              value={product.sale_count}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., 5"
                              min="0"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="in_stock"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              In Stock
                            </label>
                            <input
                              type="number"
                              name="in_stock"
                              id="in_stock"
                              value={product.in_stock}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., 5"
                              min="0"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="location"
                              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                            >
                              Location
                            </label>
                            <input
                              type="text"
                              name="location"
                              id="location"
                              value={product.location}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                              placeholder="e.g., Warehouse A"
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={updateProduct}
                  >
                    Update Product
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => {
                      setOpen(false);
                      updateModalSetting();
                    }}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}